import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  fetchEarnings,
  periodsForPlan,
  EARNINGS_PERIODS,
  fmtMoney,
  fmtMoneyShort,
} from '../lib/earnings';

/**
 * EarningsCard — self-contained card for the Daily Earnings feature.
 *
 * Props:
 *   shopId       — required.
 *   barberId     — optional. When set the card is scoped to a single barber.
 *   plan         — 'standard' | 'premium'. Controls available periods.
 *                  Pass 'premium' to bypass the cap (master admin case).
 *   title        — heading text. Defaults to "Earnings".
 *   subtitle     — secondary label under the heading.
 *   defaultPeriod — 'week' | 'month' | '3m' | '6m' | '9m' | 'year'.
 *   dataTestId   — root testid so pages can target this card.
 *
 * The card renders its OWN period dropdown independent of anything else on
 * the page — every earnings surface (Dashboard card, Analytics chart,
 * ShopDetail) gets its own filter, which is exactly what the spec asks for.
 */
export const EarningsCard = ({
  shopId,
  barberId = null,
  plan = 'standard',
  title = 'Earnings',
  subtitle = null,
  defaultPeriod = 'week',
  dataTestId = 'earnings-card',
}) => {
  const availablePeriods = useMemo(() => periodsForPlan(plan), [plan]);
  const initialPeriod = availablePeriods.find(p => p.key === defaultPeriod)
    ? defaultPeriod
    : availablePeriods[0].key;

  const [period, setPeriod] = useState(initialPeriod);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    if (!shopId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetchEarnings({ shopId, barberId, period })
      .then(r => { if (!cancelled) { setData(r); setLoading(false); } })
      .catch(e => { if (!cancelled) { setErr(e.message || String(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [shopId, barberId, period]);

  const total = data?.total ?? 0;
  const cash  = data?.cash  ?? 0;
  const card  = data?.card  ?? 0;
  const count = data?.count ?? 0;
  const series = data?.series || [];

  const periodLabel = EARNINGS_PERIODS.find(p => p.key === period)?.label || period;

  return (
    <div className="panel earnings-card" data-testid={dataTestId}>
      <div className="ph">
        <div>
          <h3>{title}</h3>
          {subtitle && (
            <div style={{ fontSize: 11.5, color: 'var(--mute)', marginTop: 2 }}>
              {subtitle}
            </div>
          )}
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="period-mini"
          data-testid={`${dataTestId}-period`}
        >
          {availablePeriods.map(p => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
      </div>

      <div className="earnings-body">
        {/* Headline number + cash/card split */}
        <div className="earnings-headline">
          <div className="earnings-total" data-testid={`${dataTestId}-total`}>
            {loading ? '—' : fmtMoney(total)}
          </div>
          <div className="earnings-split" data-testid={`${dataTestId}-split`}>
            <span className="chip cash">
              <span className="dot" />
              Cash <b>{loading ? '—' : fmtMoney(cash)}</b>
            </span>
            <span className="chip card">
              <span className="dot" />
              Card <b>{loading ? '—' : fmtMoney(card)}</b>
            </span>
            <span className="count-chip">
              {loading ? '—' : `${count} ${count === 1 ? 'booking' : 'bookings'}`}
            </span>
          </div>
          <div className="earnings-period-label">{periodLabel}</div>
        </div>

        {/* Line chart */}
        {err ? (
          <div style={{ padding: '18px 4px', color: 'var(--red, #dc2626)', fontSize: 12 }}>
            Couldn&apos;t load earnings: {err}
          </div>
        ) : (
          <EarningsLine series={series} loading={loading} testId={`${dataTestId}-chart`} />
        )}
      </div>
    </div>
  );
};

/**
 * Inline SVG line chart. Kept dependency-free to match the rest of the
 * codebase (no chart libs installed). Renders: a stroked line, a soft
 * gradient fill underneath, dots at every bucket, and X-axis labels.
 */
const EarningsLine = ({ series, loading, testId }) => {
  // Measure the wrapper so the SVG's viewBox matches the actual pixel
  // width. Without this, viewBox width=640 got stretched to fill a
  // ~900px card via `preserveAspectRatio="none"`, distorting every text
  // label horizontally. Now the SVG renders at exact pixel scale and
  // fonts stay crisp on every viewport / card size.
  const hostRef = useRef(null);
  const [W, setW] = useState(640);
  useLayoutEffect(() => {
    if (!hostRef.current) return undefined;
    const el = hostRef.current;
    const update = () => {
      const w = el.clientWidth || 640;
      // Guard against jitter: only re-render when the change is
      // meaningful (>4px) so ResizeObserver + React don't loop.
      setW(prev => (Math.abs(prev - w) > 4 ? w : prev));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = 180;
  const PAD_L = 40;
  const PAD_R = 12;
  const PAD_T = 12;
  const PAD_B = 26;

  if (loading) {
    return (
      <div ref={hostRef} style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--mute)', fontSize: 12 }}>
        Loading…
      </div>
    );
  }
  if (!series.length) {
    return (
      <div ref={hostRef} style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--mute)', fontSize: 12 }}>
        No earnings in this window.
      </div>
    );
  }

  const max = Math.max(1, ...series.map(s => s.total));
  const xw = (W - PAD_L - PAD_R) / Math.max(series.length - 1, 1);
  const yFor = (v) => PAD_T + (H - PAD_T - PAD_B) * (1 - v / max);
  const xFor = (i) => PAD_L + i * xw;

  const linePath = series.map((s, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(s.total)}`).join(' ');
  const areaPath =
    `${linePath} L ${xFor(series.length - 1)} ${H - PAD_B} L ${xFor(0)} ${H - PAD_B} Z`;

  // Y-axis ticks (0, mid, max)
  const yTicks = [0, max / 2, max];

  // Show at most ~7 x-axis labels
  const step = Math.max(1, Math.ceil(series.length / 7));

  return (
    <div ref={hostRef} style={{ width: '100%' }}>
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      width="100%"
      height={H}
      data-testid={testId}
      className="earnings-line"
    >
      <defs>
        <linearGradient id="earn-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="var(--acc)" stopOpacity="0.28" />
          <stop offset="100%" stopColor="var(--acc)" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Y-axis grid */}
      {yTicks.map((v, i) => (
        <g key={i}>
          <line
            x1={PAD_L} x2={W - PAD_R}
            y1={yFor(v)} y2={yFor(v)}
            stroke="var(--line, #e7e5e4)" strokeDasharray={i === 0 ? '' : '2 4'}
          />
          <text
            x={PAD_L - 6} y={yFor(v) + 3}
            textAnchor="end" fontSize="10"
            fill="var(--mute)"
          >
            {fmtMoneyShort(v)}
          </text>
        </g>
      ))}

      <path d={areaPath} fill="url(#earn-fill)" />
      <path d={linePath} fill="none" stroke="var(--acc)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

      {series.map((s, i) => (
        <g key={s.bucket}>
          <circle cx={xFor(i)} cy={yFor(s.total)} r="2.5" fill="var(--acc)" />
          <title>{`${s.label} · ${fmtMoney(s.total)} (${s.count} booking${s.count === 1 ? '' : 's'})`}</title>
        </g>
      ))}

      {/* X labels — first & last labels are anchored to the edge instead
          of the centre so they don't get clipped by the SVG viewBox on
          smaller widths (or on shorter periods like "week" where labels
          land right on x=0 and x=W). */}
      {series.map((s, i) => {
        if (!(i % step === 0 || i === series.length - 1)) return null;
        const anchor = i === 0 ? 'start' : (i === series.length - 1 ? 'end' : 'middle');
        return (
          <text
            key={`xl-${i}`}
            x={xFor(i)} y={H - 8}
            textAnchor={anchor} fontSize="10.5"
            fill="var(--mute)"
          >{s.label}</text>
        );
      })}
    </svg>
    </div>
  );
};

export default EarningsCard;
